import { useCallback } from "react"
import { useMutation } from "urql"

import type { TransferAttachmentsMutationParams, TransferSaveResult } from "../types"

import { useUpdateTransferDocuments } from "./useUpdateTransferDocuments"
import { useUpsertTransferComment } from "./useUpsertTransferComment"

import "../graphql/mutations"

import type { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import type {
  UpdateTransferFailure,
  UpdateTransferInput,
  UpdateTransferMutation,
  UpdateTransferMutationVariables,
  UpdateTransferSuccess,
} from "@/graphql/codegen/graphql"
import { UpdateTransferDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseUpdateTransferReturn {
  updateTransfer: (
    input: UpdateTransferInput,
    attachments?: TransferAttachmentsMutationParams
  ) => Promise<TransferSaveResult>
  isUpdating: boolean
  documentUploadStatuses: Record<string, UploadRowStatus>
  isUpdatingDocuments: boolean
}

export const useUpdateTransfer = (): UseUpdateTransferReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { upsertComment, isUpserting: isUpsertingComment } = useUpsertTransferComment()
  const { updateDocuments, documentUploadStatuses, isUpdatingDocuments } = useUpdateTransferDocuments()

  const [{ fetching }, executeUpdateTransfer] = useMutation<UpdateTransferMutation, UpdateTransferMutationVariables>(
    UpdateTransferDocument
  )

  const updateTransfer = useCallback(
    async (
      input: UpdateTransferInput,
      attachments?: TransferAttachmentsMutationParams
    ): Promise<TransferSaveResult> => {
      const { data, error } = await executeUpdateTransfer({ input })
      const output = data?.transfer.updateTransfer
      const hasError = error || output?.__typename === "UpdateTransferFailure"

      if (hasError) {
        const message = error?.message ?? (output as UpdateTransferFailure).error.message
        notify({ type: NotificationType.Error, message })

        return { transferId: undefined, hasDocumentUploadFailure: false }
      }

      const { transferId } = (output as UpdateTransferSuccess).transfer

      if (attachments?.comment?.trim()) {
        await upsertComment({
          transferId,
          value: attachments.comment,
          existingCommentId: attachments.existingCommentId,
        })
      }

      const hasDocumentUploadFailure = await updateDocuments({
        transferId,
        documentsToUpload: attachments?.documentsToUpload,
        documentsToDelete: attachments?.documentsToDelete,
      })

      notify({ type: NotificationType.Success, message: "Transfer updated" })

      return { transferId, hasDocumentUploadFailure }
    },
    [executeUpdateTransfer, upsertComment, updateDocuments, notify]
  )

  return {
    updateTransfer,
    isUpdating: fetching || isUpsertingComment || isUpdatingDocuments,
    documentUploadStatuses,
    isUpdatingDocuments,
  }
}
